/**
 * Per-tab persistence for the Copilot conversation.
 *
 * Copilot keeps the real conversation, keyed by the session id (see
 * GET /copilot/history), so surviving a reload only needs the session id plus a
 * rendering snapshot of the messages. sessionStorage is deliberate: it survives
 * reloads but a tab opened normally never sees another tab's conversation, so
 * ordinary multi-tab use cannot send two chats into one session. The one gap is
 * "Duplicate tab", which copies sessionStorage: both copies then share the
 * session, and Copilot cancels the older of two overlapping turns, until one
 * of them starts a new chat. Accepted for now.
 *
 * `pending` is set while a turn is running and cleared when it ends. Finding it
 * on load means the tab was reloaded mid-answer: the backend cancels the turn
 * on disconnect, so the UI offers to send that request again.
 */

const STORAGE_KEY = 'osavul:copilot-min:conversation';

export type CopilotTurnMode = 'qa' | 'report';

export interface StoredCopilotMessage {
  role: 'user' | 'assistant';
  content: string;
  /** An answer's steps and approval card, so a reload shows them again. */
  metadata?: { custom: Record<string, unknown> };
}

export interface StoredCopilotConversation {
  sessionId: string;
  userId: number | string;
  workspaceId: number | string;
  /** Older snapshots carried the case the chat was opened on; a chat is no longer bound to one. */
  narrativeId?: number;
  narrativeTitle?: string;
  narrativePath?: string;
  messages: StoredCopilotMessage[];
  pending: { text: string; mode: CopilotTurnMode } | null;
}

export function readCopilotConversation(): StoredCopilotConversation | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredCopilotConversation;
    if (!stored?.sessionId || !Array.isArray(stored.messages)) {
      sessionStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return stored;
  } catch {
    return null;
  }
}

export function writeCopilotConversation(conversation: StoredCopilotConversation): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(conversation));
  } catch {
    // Quota or private mode: the chat keeps working, it just won't survive a reload.
  }
}

export function clearCopilotConversation(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean up if storage is unavailable.
  }
}
